export interface InformationsBaseDeDonnees {
  serveur?: string;
  baseDeDonnees?: string;
  utilisateur?: string;
}

export interface ModificationConnectionString {
  identifiantTenant: string;
  indexTenant: number;
  nomConnectionString: string;
  serveur: string;
  baseDeDonnees: string;
  utilisateur?: string;
  motDePasse?: string;
  // Reprise annuaire : le mot de passe legacy est lu côté serveur, jamais par le navigateur
  motDePasseDepuisAnnuaire?: boolean;
}

// Connexion portée par la fiche annuaire (le mot de passe n'est jamais exposé)
export interface ConnexionAnnuaire {
  serveur?: string;
  baseDeDonnees?: string;
  utilisateur?: string;
  motDePassePresent: boolean;
}

export interface Tenant {
  index: number;
  identifier: string;
  databaseName?: string;
  managerIsEnabled: boolean;
  meetchIsEnabled: boolean;
  isOrange: boolean;
  isSkidata: boolean;
  isEventim: boolean;
}

// Payload unique de validation : le backend écrit le tenant (source de vérité) et
// reflète vers l'annuaire legacy tant qu'il existe
export interface ModificationClient extends Tenant {
  label?: string;
  disabled: boolean;
  isActiveArchiving: boolean;
  meetchApiKey?: string;
}

// Champ dont le tenant et l'annuaire legacy divergent ("true"/"false" pour les booléens).
// valeurAnnuaire reste absente pour les champs secrets : seule la divergence est signalée
export interface Desynchronisation {
  champ: string;
  valeurAnnuaire?: string;
}

// La clé API Meetch est un secret : le backend n'en renvoie que la présence. Le champ du
// formulaire reste modifiable (écriture seule), comme le mot de passe de la base
export interface ClientComplet extends Omit<ModificationClient, 'meetchApiKey'> {
  meetchApiKeyPresente: boolean;
  actifSurBackstage: boolean;
  possedeFicheAnnuaire: boolean;
  desynchronisations: Desynchronisation[];
  connexionAnnuaire?: ConnexionAnnuaire;
}

// Tick&Live Connect : partenaires API d'une famille d'étiquettes ({famille}:{env}), clés
// Connect:PartnerConfigurations. Un accès est un client et les codes des interfaces Aparté du
// partenaire sur ce client (une par lieu ou par marque). codeInterfaces : valeurs stockées, telles
// quelles, dans l'ordre de leur index
export interface TenantPartenaireConnect {
  index: number;
  tenantId?: string;
  codeInterfaces: string[];
}

export interface PartenaireConnect {
  index: number;
  partnerName?: string;
  clientId?: string;
  tenants: TenantPartenaireConnect[];
}

// Accès à écrire : index null = activation d'un nouveau client. La liste des codes remplace celle
// stockée
export interface AccesPartenaireConnect {
  index: number | null;
  tenantId: string;
  codeInterfaces: number[];
}

// Soit le partenaire lui-même (sans accès ; index null = création, le backend attribue le premier
// index libre), soit l'accès d'un client chez un partenaire existant. Comme pour les fronts, un
// accès part sans libellé ni client id : renvoyés tels qu'affichés, ils écraseraient une
// modification faite ailleurs, et le backend les ignore
export type EnregistrementPartenaireConnect =
  | { index: number | null; partnerName: string; clientId?: string; tenants: [] }
  | { index: number; tenants: [AccesPartenaireConnect] };

// Type d'un service Tick&Live Connect (famille d'étiquettes, donc une API) : partenaires avec
// interface ITR (Connect:PartnerConfigurations, comme TempsReel), applications client
// (Connect:ClientConfigurations, comme Crm) ou fronts web (Connect:FrontConfigurations, comme VenteWeb)
export type NatureServiceConnect = 'partenaire' | 'application' | 'front';

// Tick&Live Connect : applications sans interface d'une famille d'étiquettes ({famille}:{env}),
// clés Connect:ClientConfigurations. Configuration de base du framework (UseConnectClient) :
// une application et les clients auxquels elle a accès
//
// visibilite et loginId sont les valeurs stockées, telles quelles : absentes ou invalides sur un
// accès antérieur à la visibilité, que l'écran signale (accesAVisibiliteValide)
export interface TenantApplicationConnect {
  index: number | null;
  tenantId?: string;
  visibilite?: string;
  loginId?: string;
}

// Visibilité d'un accès d'application client : Restricted borne la lecture aux droits Aparté du
// login loginId du tenant, Unrestricted ne porte pas de login
export type VisibiliteConnect = 'Restricted' | 'Unrestricted';

export interface AccesApplicationConnect {
  visibilite: VisibiliteConnect;
  // présent seulement si Restricted
  loginId?: number;
}

export interface ApplicationConnect {
  index: number;
  clientName?: string;
  clientId?: string;
  tenants: TenantApplicationConnect[];
}

// L'application seulement (libellé, client id) : ses accès se donnent un par un
// (accorderAccesApplicationConnect$). index null = création, le backend attribue le premier index libre
export interface EnregistrementApplicationConnect {
  index: number | null;
  clientName: string;
  clientId?: string;
}

// Tick&Live Connect : fronts web d'une famille d'étiquettes ({famille}:{env}), clés
// Connect:FrontConfigurations (UseConnectFront). Un accès ne porte que le client : l'api lit les
// droits Aparté dans la configuration web du tenant
export interface TenantFrontConnect {
  index: number | null;
  tenantId?: string;
}

export interface FrontConnect {
  index: number;
  frontName?: string;
  clientId?: string;
  tenants: TenantFrontConnect[];
}

// Le front seulement (libellé, client id). index null = création
export interface EnregistrementFrontConnect {
  index: number | null;
  frontName: string;
  clientId?: string;
}

export type SensSynchronisation = 'annuaireVersTenant' | 'tenantVersAnnuaire';

// Synchronisation complète (options + connection string) dans le sens choisi
export interface Resynchronisation {
  identifiant: string;
  index: number;
  sens: SensSynchronisation;
}

// Fiche de l'annuaire legacy sans tenant, candidate à l'import
export interface InstitutionImportable {
  codeInstit: string;
  label?: string;
  disabled: boolean;
  isActiveArchiving: boolean;
  meetchApiKey?: string;
  serveur?: string;
  baseDeDonnees?: string;
  utilisateur?: string;
  motDePassePresent: boolean;
  baseExistanteDansRegistre: boolean;
}

export interface NouvelleBase {
  storeName: string;
  serveur: string;
  baseDeDonnees: string;
  utilisateur?: string;
  motDePasse?: string;
  motDePasseDepuisAnnuaire: boolean;
}

export interface ImportClient {
  codeInstit: string;
  // renseigné uniquement si la base de la fiche n'existe pas dans le registre
  baseACreer?: NouvelleBase;
}

// Un nouveau client est toujours créé actif et sans archivage : le backend force
// disabled/isActiveArchiving à false. La base est soit existante (databaseName), soit à créer
// (baseACreer : le registre et le secret Key Vault sont créés — pas de reprise annuaire
// possible puisque la fiche n'existe pas encore)
export interface CreationClient {
  identifiant: string;
  label?: string;
  databaseName?: string;
  baseACreer?: Omit<NouvelleBase, 'motDePasseDepuisAnnuaire'>;
  managerIsEnabled: boolean;
  meetchIsEnabled: boolean;
  isOrange: boolean;
  isSkidata: boolean;
  isEventim: boolean;
  meetchApiKey?: string;
}

// Bornes de saisie, pendant front de LongueursMaximales côté backend : les deux doivent
// rester alignés. Elles évitent d'envoyer au serveur une valeur qu'il refusera, et surtout
// d'écrire une modification que le journal d'audit ne pourra pas enregistrer
export const LONGUEURS_MAXIMALES = {
  identifiant: 100,
  label: 200,
  nomBaseDeDonnees: 128,
  utilisateur: 128,
  serveur: 255,
  nomService: 50,
  nomPartenaire: 200,
  valeurInterfaceConnect: 200,
  // Nombre de codes interface d'un accès partenaire : le journal d'audit les liste tous
  codesInterfaceParAcces: 50,
  // Mots de passe et clés d'API : leur valeur n'atteint jamais le journal d'audit, mais elle
  // part dans le Key Vault et l'annuaire legacy
  secret: 500,
} as const;

// Historique des modifications. identifiantClient absent = action globale (configuration
// Tick&Live Connect) : son impact vaut pour tous les clients, elle remonte donc même quand
// le tableau est filtré sur un client précis
export interface ActionHistorisee {
  dateAction: string;
  environnement: string;
  identifiantClient?: string;
  action: string;
  utilisateur: string;
  description: string;
}

export interface PageHistorique {
  items: ActionHistorisee[];
  totalCount: number;
}

// État du provisionnement des fils de l'eau sur la base Aparté du client, rendu par le
// script de vérification embarqué côté backend. `actif` est le flag du tenant,
// `provisionne` l'état réel de la base : les deux peuvent diverger, et c'est cette
// divergence que la fiche doit montrer
export interface EtatFilsDeLEau {
  baseDeDonnees: string;
  // Colonnes de rattachement du schéma Aparté : leur absence demande un DBA, aucun
  // script de fil de l'eau ne les crée
  prerequisSatisfaits: boolean;
  socleProvisionne: boolean;
  partenaires: EtatPartenaireFilDeLEau[];
}

export interface EtatPartenaireFilDeLEau {
  partenaire: string;
  actif: boolean;
  provisionne: boolean;
  manquants: ObjetFilDeLEauManquant[];
}

// `detail` est le libellé rendu par le script de vérification : affiché tel quel
export interface ObjetFilDeLEauManquant {
  objet: string;
  detail?: string;
}

// Reprovisionnement manuel : rejoue la convergence pour les fils de l'eau que le tenant porte
// déjà, sur une base dont le diagnostic montre une divergence. L'index protège d'un
// identifiant recyclé ; les partenaires ne sont pas envoyés, le backend les lit sur le tenant
export interface ReprovisionnementFilsDeLEau {
  identifiant: string;
  index: number;
}
